import type { Language } from '@/types';
import { getTranslation } from '@/utils/translations';
import { storage, debounce } from '@/utils/helpers';

class LanguageSwitcher {
  private currentLanguage: Language = 'fr';
  private readonly STORAGE_KEY = 'devmeup-language';
  private readonly debouncedSetLanguage: (lang: Language) => void;
  private isSetup = false;

  constructor() {
    this.debouncedSetLanguage = debounce(
      (...args: unknown[]) => this.setLanguage(args[0] as Language),
      100
    );
    this.init();
  }

  private init(): void {
    // Load saved language or detect browser language
    const savedLanguage = storage.get<Language>(this.STORAGE_KEY);
    const browserLanguage = navigator.language.startsWith('fr') ? 'fr' : 'en';

    this.currentLanguage = savedLanguage || browserLanguage;

    // Setup with multiple retry attempts to handle HTML injection timing
    this.setupWithRetries();
  }

  private setupWithRetries(): void {
    if (this.isSetup) return;

    let attempts = 0;
    const maxAttempts = 10;
    const retryDelay = 200;

    const trySetup = (): void => {
      if (this.isSetup) return;

      attempts++;
      const langToggle = document.getElementById('lang-toggle-btn');

      if (langToggle) {
        // Found elements, set up everything
        this.isSetup = true;
        this.setLanguage(this.currentLanguage);
        this.updateUI(this.currentLanguage);
        this.setupEventListeners();
      } else if (attempts < maxAttempts) {
        // Retry after delay
        setTimeout(trySetup, retryDelay);
      }
    };

    // Start immediately and on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', trySetup);
    } else {
      trySetup();
    }

    // Also try after a short delay for HTML injection
    setTimeout(trySetup, 100);
  }

  private setupEventListeners(): void {
    const langToggleId = 'lang-toggle-btn';
    const langDropdownId = 'lang-dropdown';

    // Global click listener for delegation
    document.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;

      // 1. Toggle Button Click
      const toggleBtn = target.closest(`#${langToggleId}`);
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
        this.toggleDropdown(!isExpanded);
        return;
      }

      // 2. Option Selection Click
      const optionBtn = target.closest('.lang-option') as HTMLButtonElement;
      if (optionBtn) {
        e.preventDefault();
        e.stopPropagation();
        const lang = optionBtn.dataset.lang as Language;

        if (lang && lang !== this.currentLanguage) {
          this.switchLanguage(lang);
        }
        this.toggleDropdown(false);
        return;
      }

      // 3. Click Outside - Close Dropdown
      const dropdown = document.getElementById(langDropdownId);
      const toggle = document.getElementById(langToggleId);

      if (dropdown && toggle && !dropdown.contains(target) && !toggle.contains(target)) {
        // Only close if it's actually open to avoid unnecessary DOM updates
        if (toggle.getAttribute('aria-expanded') === 'true') {
          this.toggleDropdown(false);
        }
      }
    });
  }

  private toggleDropdown(show: boolean): void {
    const langToggle = document.getElementById('lang-toggle-btn');
    const langDropdown = document.getElementById('lang-dropdown');
    const arrow = document.getElementById('lang-arrow');

    if (!langToggle || !langDropdown) return;

    langToggle.setAttribute('aria-expanded', show ? 'true' : 'false');

    if (show) {
      langDropdown.classList.remove('opacity-0', 'scale-95', 'pointer-events-none');
      langDropdown.classList.add('opacity-100', 'scale-100', 'pointer-events-auto');
      if (arrow) arrow.style.transform = 'rotate(180deg)';
    } else {
      langDropdown.classList.add('opacity-0', 'scale-95', 'pointer-events-none');
      langDropdown.classList.remove('opacity-100', 'scale-100', 'pointer-events-auto');
      if (arrow) arrow.style.transform = 'rotate(0deg)';
    }
  }

  private switchLanguage(lang: Language): void {
    this.currentLanguage = lang;
    storage.set(this.STORAGE_KEY, lang);
    this.debouncedSetLanguage(lang);
    this.updateUI(lang);
    this.updateDocumentLanguage(lang);

    // Dispatch custom event for other components
    const event = new CustomEvent('languageChange', { detail: lang });
    document.dispatchEvent(event);
  }

  private setLanguage(lang: Language): void {
    // Update navigation links
    this.updateNavigation(lang);

    // Update all elements with data-translate attributes
    this.updateTranslatedElements(lang);

    // Update placeholder attributes
    this.updatePlaceholders(lang);

    // Dispatch custom event for other components
    window.dispatchEvent(
      new CustomEvent('languageChanged', {
        detail: { language: lang },
      })
    );
  }

  private updateNavigation(lang: Language): void {
    const navSelectors = ['.md\\:flex a', '#mobile-menu a'];

    navSelectors.forEach(selector => {
      const links = document.querySelectorAll(selector);
      if (links.length >= 4) {
        const keys = ['about', 'services', 'team', 'contact'];
        links.forEach((link, index) => {
          if (keys[index]) {
            (link as HTMLElement).textContent = getTranslation(lang, keys[index]);
          }
        });
      }
    });
  }

  private updateTranslatedElements(lang: Language): void {
    document.querySelectorAll('[data-translate]').forEach(element => {
      const key = element.getAttribute('data-translate');
      if (key) {
        const translation = getTranslation(lang, key);
        (element as HTMLElement).innerHTML = translation;
      }
    });
  }

  private updatePlaceholders(lang: Language): void {
    document.querySelectorAll('[data-translate-placeholder]').forEach(element => {
      const key = element.getAttribute('data-translate-placeholder');
      if (key) {
        const translation = getTranslation(lang, key);
        (element as HTMLInputElement | HTMLTextAreaElement).placeholder = translation;
      }
    });
  }

  private updateUI(lang: Language): void {
    // Update Active Option Styling
    document.querySelectorAll('.lang-option').forEach(btn => {
      const button = btn as HTMLButtonElement;
      const isSelected = button.dataset.lang === lang;
      const checkIcon = button.querySelector('.check-icon');

      if (isSelected) {
        button.classList.add('bg-blue-50', 'text-blue-600', 'group', 'active');
        button.classList.remove('text-gray-700');
        if (checkIcon) {
          checkIcon.classList.remove('opacity-0');
          checkIcon.classList.add('opacity-100');
        }
      } else {
        button.classList.remove('bg-blue-50', 'text-blue-600', 'group', 'active');
        button.classList.add('text-gray-700');
        if (checkIcon) {
          checkIcon.classList.remove('opacity-100');
          checkIcon.classList.add('opacity-0');
        }
      }
    });

    // Update Toggle Button Text
    const currentLangText = document.getElementById('current-lang-text');
    if (currentLangText) {
      currentLangText.textContent = lang.toUpperCase();
    }
  }

  private updateDocumentLanguage(lang: Language): void {
    document.documentElement.lang = lang;

    // Update meta tags for better SEO
    const description = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (description) {
      const key = lang === 'fr' ? 'meta-description-fr' : 'meta-description-en';
      const metaDescriptions = {
        'meta-description-fr':
          'DevMeUp, spécialistes en développement web et mobile, créons des solutions digitales innovantes adaptées à vos besoins.',
        'meta-description-en':
          'DevMeUp, specialists in web and mobile development, we create innovative digital solutions adapted to your needs.',
      };
      description.content =
        metaDescriptions[key as keyof typeof metaDescriptions] || description.content;
    }
  }

  public getCurrentLanguage(): Language {
    return this.currentLanguage;
  }
}

export default LanguageSwitcher;
